import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
    Button,
    Modal,
    ModalComponent,
    ModalProps,
    notify,
    SelectField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import {
    TenantsUserRelationship,
    UpdateSettingsDocument,
} from '@graphql/generated/graphql';

import { defaultSelectOption, relationshipOptions } from '@utils/dictionary';
import { useUserOptions } from '@utils/hooks/useUserOptions';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { requiredSelectV2 } from '@shared/validations';

interface RelationshipChangeModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const RelationshipChangeModal = ({
    onClose,
    open,
}: RelationshipChangeModalProps) => {
    const { t } = useTranslation();
    const { data } = useUserOptions();

    const {
        control,
        formState: { errors, isDirty, isSubmitting },
        handleSubmit,
        reset,
    } = useForm<ChangeRelationshipFormValues>({
        defaultValues: {
            relationship: defaultSelectOption(
                relationshipOptions(),
                data?.user.relationship,
            ),
        },
        mode: 'onChange',
        resolver: yupResolver(ChangeRelationshipSchema),
    });

    const [updateSettings, { loading }] = useMutation(UpdateSettingsDocument, {
        context: {
            replaceTokens: true,
        },
        onCompleted: () => {
            reset();
            onClose();
            notify(t('CHANGE_RELATIONSHIP_SUCCESS')).SUCCESS();
        },
        onError: () => {},
    });

    const handleCancel = () => {
        reset();
        onClose();
    };

    const handleRelationshipChange = (data: ChangeRelationshipFormValues) => {
        void updateSettings({
            variables: {
                input: {
                    relationship: data.relationship
                        .value as TenantsUserRelationship,
                },
            },
        });
    };

    const { field: relationshipSelect } = useController({
        control,
        name: 'relationship',
    });

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CHANGE_RELATIONSHIP')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {t('SETTINGS_CHANGE_RELATIONSHIP_TEXT')}
                    </Modal.Description>
                </Modal.Header>

                <StandardForm>
                    <SelectField
                        {...relationshipSelect}
                        error={!!errors.relationship}
                        label={t('RELATIONSHIP_TO_COMPANY')}
                        message={t(errors.relationship?.message || '')}
                        options={relationshipOptions()}
                        placeholder={t('RELATIONSHIP_TO_COMPANY_PLACEHOLDER')}
                        testId="select_change-relationship"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={!isDirty || isSubmitting}
                        isLoading={isSubmitting || loading}
                        onClick={handleSubmit(handleRelationshipChange)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ChangeRelationshipSchema = yup.object({
    relationship: requiredSelectV2,
});

type ChangeRelationshipFormValues = yup.InferType<
    typeof ChangeRelationshipSchema
>;
